import request from '@/utils/request'
import { BASE_API, COMMON_API, IS_GLOBAL } from '@/const'
import useBaseStore from '@/store/useBaseStore'

export type GuideAttachement = {
  id: string //ID
  appName: string //应用
  port: string //口岸
  shipCompanyCode: string //船司代码
  shipCompanyName: string //船司名称
  shipAgentCode: string //船代代码
  shipAgentName: string //船代名称
  type: string //类型(counter_delete:柜台删单)
  fileUrl: string //文件地址
  fileName: string //文件名
  remark: string //备注
  uploadBy: string //上传人
  uploadTime: string //上传时间
}

/**
 * @description: 查询提单头
 * @return {Promise<DataResponse<any>>}
 */
export const fetchVerifyBillHead = async (
  shipCompanyCode: string,
  billNo: string,
): Promise<any> => {
  const res = await request<any>(
    '/all-port/storybrooke-assistor/bill_head_verify/validBillHead',
    {
      method: 'post',
      data: {
        shipCompanyCode,
        billNo,
        appName: useBaseStore().getProductCode,
        port: '',
      },
    },
  )
  return res.data
}

/**
 * @description: 查询使用指南 & 服务协议
 * @return {Promise<DataResponse<GuideAttachement>>}
 */
export const fetchUserGuideAttachment = async (params: {
  appName: string
  port: string
  type: string
}): Promise<DataResponse<GuideAttachement>> => {
  const res = await request<DataResponse<GuideAttachement>>(
    '/all-port/storybrooke-assistor/business-data/get-use-guide',
    {
      params,
    },
  )
  return res.data
}

/**
 * @description: 查询业务资料
 * @return {Promise<DataResponse<GuideAttachement>>}
 */
export const fetchBusinessAttachmentRecords = async (): Promise<
  DataResponse<GuideAttachement>
> => {
  const res = await request<DataResponse<GuideAttachement>>(
    '/storybrooke-manifest-assistor/client/business-data/get-business-dat-page',
  )
  return res.data
}

/**
 * @description 获取下拉数据
 * @param {string} excludeDictType 排除的类型
 * @return {Promise<any>}
 */
export const fetchDropDownList = async (
  excludeDictType?: string,
): Promise<any> => {
  const baseStore = useBaseStore()
  const res = await request<DataResponse<Record<string, DictRaw>>>(
    `/all-port/storybrooke-assistor/dictionary/back/dropdown-data?appName=${baseStore.getProductCode}&port=${excludeDictType ? `&excludeDictType=${excludeDictType}` : ''}`,
  )
  return res.data
}
/**
 * @description 获取单个下拉数据
 * @param {string} dictType 类型
 * @return {Promise<any>}
 */
export const fetchDropDownListByDictType = async (
  dictType?: string,
): Promise<any> => {
  const baseStore = useBaseStore()
  const res = await request<DataResponse<Record<string, DictRaw>>>(
    `/all-port/storybrooke-assistor/dictionary/select?appName=${baseStore.getProductCode}&port=${dictType ? `&dictType=${dictType}` : ''}`,
  )
  return res.data
}

export type PriceRate = {
  id: string // id
  rateType: number // 费率类型(0：分单，1：主单，2：箱)
  rateName: string // 费率名称(杂费/港务费/港口设施保安费/VGM/CHC/预配舱单/刷条码/THC/文件费)
  rateCode: string // 费率code(对应费率名称)
  rate: number // 费率(1000倍数)
}

export type PriceRecord = {
  port: string //港口（上海：CNSHA，青岛：CNQIN、天津：CNTNJ）
  shipCompanyCode: string // 船公司代码
  shipCompanyName: string // 船公司名称
  boxType: string // 箱型
  rateInfoColumns: PriceRate[]
  rateTotal: number // 总费率
}

export type VerifyCodeReq = {
  ticket: string
  randstr: string
  phone: string
}

export type ApplyReq = {
  code: string
  enterpriseName: string
  phone: string
  secret: string
  source: number
}
/**
 * @description 保存线索
 * @return {Promise<DataResponse<Record<string, DictRaw>>>}
 */
export const saveClue = async (
  data: ApplyReq,
): Promise<DataResponse<PriceRecord[]>> => {
  const url = IS_GLOBAL
    ? '/all-port/storybrooke-assistor/clue/save'
    : '/aci/api/landing-page-clue/submit'
  const res = await request<DataResponse<any>>(url, {
    method: 'post',
    data,
  })
  return res.data
}

/**
 * @description 获取短信验证码
 * @return {Promise<DataResponse<Record<string, DictRaw>>>}
 */
export const fetchPhoneCode = async (
  data: VerifyCodeReq,
): Promise<DataResponse<PriceRecord[]>> => {
  const res = await request<DataResponse<any>>('/aci/anonymous/sms/send', {
    method: 'post',
    data,
    baseURL: import.meta.env.VITE_API_HOST,
  })
  return res.data
}

export const downloadTemplate = async (
  businessType: 'MPCI_HBL' | 'MPCI_MBL',
  type: 'S' | 'M',
  port: string = '',
) => {
  const baseStore = useBaseStore()
  const res: any = await request.get<DataResponse<any>>(
    '/all-port/storybrooke-assistor/pass/template/findByBusinessType',
    {
      params: {
        appName: baseStore.getProductCode,
        port,
        businessType,
        type,
      },
    },
  )
  const { fileId } = res.data[0] || {}
  if (fileId) {
    const url = `https:${import.meta.env.VITE_NAS_URL}/pass/proxy/${fileId}`
    location.href = url
  } else {
    ElMessage.error(`模板未配置`)
  }
}

/**
 * @description: 获船名航次查船期有缓存
 * @return {Promise<DataResponse<any>>}
 */
export const fetchVesselShipments = async (params: {
  vesselName: string
  voyageNo: string
}): Promise<any> => {
  const res = await request.get(
    '/freightower/vessel2/getVesselShipments/cache',
    {
      baseURL: BASE_API,
      params,
    },
  )
  return res.data
}

/**
 * 童话镇-适配器数据转换接口
 * @param {string} formCode: 童话镇动态表单code
 * @param {string} productCode: 童话镇产品code
 * @param {string} flowId: 数据流id
 * @param {string} stageId: 标准数据id
 * @returns
 */
export async function fetchModuleStandardAdapter({
  flowId,
  stageId,
}: {
  flowId: string
  stageId: string
}) {
  const baseStore = useBaseStore()
  const params = {
    formCode: baseStore.getFormListCode,
    productCode: baseStore.getProductCode,
    flowId,
    stageId,
  }
  const res = await request<DataResponse<any>>(
    '/module-standard-adapter/dataFlow/businessData',
    {
      params,
    },
  )
  return res.data
}

/**
 * @description: AI识别地址
 * @param {any} data
 * @return {Promise<DataResponse<any>>}
 */
export const aiResolutionAddress = async (data: {
  content: string
  promptCode?: string
}): Promise<DataResponse<any>> => {
  const baseStore = useBaseStore()
  data.promptCode = data.promptCode || baseStore.getPromptCode.partyOnly
  const res = await request.post<DataResponse<any>>('/party/ai', data, {
    baseURL: COMMON_API,
  })
  return res.data
}

/**
 * @description: 获取最新使用的地址
 * @param {any} params
 * @return {Promise<DataResponse<any>>}
 */
export const getLatestAddress = async (params: {
  partyType: string
  pageSize: number
  pageNum: number
}): Promise<DataResponse<any>> => {
  const res = await request<DataResponse<any>>('/party/latest', {
    params,
    baseURL: COMMON_API,
  })
  return res.data
}

/**
 * @description: 根据字段获取常用选项
 * @param {any} params
 * @return {Promise<DataResponse<any>>}
 */
export const getCommonOption = async (params: {
  types: string
}): Promise<DataResponse<any>> => {
  const res: any = await request<DataResponse<any>>(
    '/user/common-option/options',
    {
      params,
      baseURL: COMMON_API,
    },
  )
  return res.data[params.types]?.options || []
}

export default {
  fetchVerifyBillHead,
  fetchUserGuideAttachment,
  fetchBusinessAttachmentRecords,
  fetchDropDownList,
  fetchDropDownListByDictType,
  saveClue,
  fetchPhoneCode,
  downloadTemplate,
  fetchVesselShipments,
  fetchModuleStandardAdapter,
  aiResolutionAddress,
  getLatestAddress,
  getCommonOption,
}
